// Shared Welcome Bonus state — one network source for Header badge + Wallet/payout.

import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import { isBlockingClaimErrorCode } from './signupBonusGating';
import { resetWelcomePromptVisit } from './welcomeBonusPrompt';
import {
  claimSignupBonus,
  fetchSignupBonus,
  DAILY_BUDGET_USER_MESSAGE,
  type SignupBonus,
} from './signupBonusService';

export type SignupBonusStoreState = {
  bonus: SignupBonus | null;
  loading: boolean;
  claiming: boolean;
  claimError: string | null;
  /** Code of the last failed claim; a blocking code keeps the claim button disabled. */
  claimErrorCode: string | null;
};

type Listener = () => void;

const listeners = new Set<Listener>();

let state: SignupBonusStoreState = {
  bonus: null,
  loading: true,
  claiming: false,
  claimError: null,
  claimErrorCode: null,
};

let prevStatus: SignupBonus['status'] | null = null;
let inflightLoad: Promise<void> | null = null;
let hasLoadedOnce = false;
let balanceRefreshBound = false;
let authChangeBound = false;

function emit() {
  listeners.forEach((l) => l());
}

function setState(patch: Partial<SignupBonusStoreState>) {
  state = { ...state, ...patch };
  emit();
}

function applyBonusResult(data: SignupBonus | null) {
  const prev = prevStatus;
  const shouldRefreshBalance =
    (prev === 'active' &&
      (data?.status === 'expired' ||
        data?.status === 'completed' ||
        data?.status === 'in_review')) ||
    (prev === 'in_review' && data?.status === 'completed') ||
    (prev == null && data?.status === 'expired');

  prevStatus = data?.status ?? null;

  let claimError = state.claimError;
  let claimErrorCode = state.claimErrorCode;
  if (data?.status === 'inactive' && data.claimBlockCode === 'DAILY_BUDGET') {
    claimError = data.claimBlockMessage || DAILY_BUDGET_USER_MESSAGE;
  } else if (data?.status !== 'inactive') {
    claimError = null;
    claimErrorCode = null;
  }

  setState({ bonus: data, claimError, claimErrorCode, loading: false });

  if (shouldRefreshBalance) {
    window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
  }
}

async function loadInternal(forceRefresh: boolean) {
  try {
    const data = await fetchSignupBonus(forceRefresh);
    applyBonusResult(data);
  } catch {
    setState({ bonus: null, loading: false });
  }
}

export function getSignupBonusSnapshot(): SignupBonusStoreState {
  return state;
}

export function subscribeSignupBonus(listener: Listener): () => void {
  listeners.add(listener);
  ensureSignupBonusStoreStarted();
  return () => {
    listeners.delete(listener);
  };
}

/** Idempotent boot: first subscriber loads once; binds a single balance-refresh listener. */
export function ensureSignupBonusStoreStarted(): void {
  if (typeof window === 'undefined') return;
  if (!balanceRefreshBound) {
    balanceRefreshBound = true;
    window.addEventListener(BALANCE_REFRESH_EVENT, onBalanceRefresh);
  }
  if (!authChangeBound) {
    authChangeBound = true;
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuthTokenChanged);
  }
  // Soft: first subscriber loads; later subscribers reuse store / in-flight.
  void loadSignupBonus(false);
}

function onBalanceRefresh() {
  void loadSignupBonus(true);
}

/**
 * The store outlives the session (module singleton, logout does not reload the page): drop the
 * previous player's bonus on sign-out, and fetch the new player's on sign-in.
 */
function onAuthTokenChanged() {
  if (!apiService.isAuthenticated()) {
    resetWelcomePromptVisit();
    prevStatus = null;
    hasLoadedOnce = false;
    setState({ bonus: null, loading: false, claiming: false, claimError: null, claimErrorCode: null });
    return;
  }
  void loadSignupBonus(true);
}

/**
 * Shared load — concurrent callers share one in-flight promise.
 * Soft calls after the first completed load are no-ops; forceRefresh always hits the API.
 */
export function loadSignupBonus(forceRefresh = false): Promise<void> {
  if (inflightLoad) return inflightLoad;
  if (!forceRefresh && hasLoadedOnce) return Promise.resolve();

  if (!hasLoadedOnce) setState({ loading: true });

  inflightLoad = loadInternal(true).finally(() => {
    hasLoadedOnce = true;
    inflightLoad = null;
  });
  return inflightLoad;
}

export async function claimSignupBonusShared(): Promise<SignupBonus | null> {
  setState({ claiming: true, claimError: null, claimErrorCode: null });
  try {
    const data = await claimSignupBonus();
    prevStatus = data?.status ?? null;
    setState({ bonus: data, claiming: false });
    window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
    return data;
  } catch (err: unknown) {
    const e = err as {
      message?: string;
      error?: string | { message?: string; code?: string };
      data?: { error?: { message?: string; code?: string }; message?: string };
    };
    const code =
      (typeof e.error === 'object' && e.error?.code) || e.data?.error?.code;
    // The claim was refused by a gate another tap cannot pass: refresh status so the
    // badge / button reflect it (e.g. the shop switched the bonus off meanwhile).
    if (isBlockingClaimErrorCode(code)) void loadSignupBonus(true);
    if (code === 'DAILY_BUDGET') {
      setState({ claiming: false, claimError: DAILY_BUDGET_USER_MESSAGE, claimErrorCode: code });
      throw err;
    }
    const nested =
      (typeof e.error === 'object' && e.error?.message) ||
      e.data?.error?.message ||
      e.data?.message;
    const msg =
      nested || e.message || 'Unable to activate welcome bonus. Please try again later.';
    setState({ claiming: false, claimError: msg, claimErrorCode: code || null });
    throw err;
  }
}

export function setSignupBonusClaimError(message: string | null) {
  setState(message == null ? { claimError: null, claimErrorCode: null } : { claimError: message });
}

/** Test helper — reset module singleton between Jest cases. */
export function __resetSignupBonusStoreForTests() {
  if (balanceRefreshBound && typeof window !== 'undefined') {
    window.removeEventListener(BALANCE_REFRESH_EVENT, onBalanceRefresh);
  }
  if (authChangeBound && typeof window !== 'undefined') {
    window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuthTokenChanged);
  }
  state = {
    bonus: null,
    loading: true,
    claiming: false,
    claimError: null,
    claimErrorCode: null,
  };
  prevStatus = null;
  inflightLoad = null;
  hasLoadedOnce = false;
  balanceRefreshBound = false;
  authChangeBound = false;
  listeners.clear();
}
